import { useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, initials } from "../lib/api";
import {
  Plane, Hotel, Briefcase, MapPin, Plus, Trash2, X, Loader2,
  Wrench, ListChecks, Users2, CheckCircle2, Circle, Truck, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";

const MISSION_STATUSES = [
  { v: "planifiee", l: "Planifiée", c: "bg-amber-100 text-amber-900" },
  { v: "en_cours", l: "En cours", c: "bg-blue-100 text-blue-900" },
  { v: "terminee", l: "Terminée", c: "bg-emerald-100 text-emerald-900" },
  { v: "annulee", l: "Annulée", c: "bg-slate-100 text-slate-600" },
];

const EQ_CATEGORIES = [
  { v: "topographie", l: "Topographie" },
  { v: "geotechnique", l: "Géotechnique" },
  { v: "informatique", l: "Informatique" },
  { v: "vehicule", l: "Véhicule" },
  { v: "securite", l: "Sécurité" },
  { v: "autre", l: "Autre" },
];

const EQ_STATUSES = [
  { v: "disponible", l: "Disponible", c: "bg-emerald-100 text-emerald-900" },
  { v: "en_mission", l: "En mission", c: "bg-blue-100 text-blue-900" },
  { v: "maintenance", l: "Maintenance", c: "bg-amber-100 text-amber-900" },
  { v: "hs", l: "Hors service", c: "bg-red-100 text-red-900" },
  { v: "perdu", l: "Perdu", c: "bg-red-100 text-red-900" },
];

export default function Logistics() {
  const [tab, setTab] = useState("missions");
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/logistics/summary"), api.get("/projects")]).then(([s, p]) => {
      setSummary(s.data); setProjects(p.data);
    });
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Opérations terrain</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Logistique</h1>
        <p className="text-sm text-slate-500 mt-1">Missions, déplacements, hébergements & matériel</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          <Stat label="Missions" value={summary.missions_total} icon={Briefcase} />
          <Stat label="Déplacements" value={summary.deplacements_total} icon={Plane} />
          <Stat label="Hébergements" value={summary.hebergements_total} icon={Hotel} />
          <Stat label="Matériel total" value={summary.materiel_total} icon={Wrench} />
          <Stat label="Mat. en mission" value={summary.materiel_en_mission} icon={Truck} accent />
          <Stat label="Coût logistique" value={fmtMoney(summary.cout_logistique_total)} mono />
        </div>
      )}

      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        <TabBtn active={tab === "missions"} onClick={() => setTab("missions")} icon={Briefcase} testid="log-tab-missions">Missions</TabBtn>
        <TabBtn active={tab === "equipment"} onClick={() => setTab("equipment")} icon={Wrench} testid="log-tab-equipment">Inventaire matériel</TabBtn>
      </div>

      {tab === "missions" && <MissionsTab projects={projects} />}
      {tab === "equipment" && <EquipmentTab />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, mono }) {
  return (
    <div className={`bg-white p-4 ${accent ? "border-l-2 border-btp-terra" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        {Icon && <Icon size={14} className="text-slate-400" />}
      </div>
      <div className={`mt-1.5 font-heading text-xl font-bold tracking-tight ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${active ? "text-btp-navy border-btp-navy" : "text-slate-500 border-transparent hover:text-slate-700"}`}>
      <Icon size={15} /> {children}
    </button>
  );
}

function MissionStatusBadge({ status }) {
  const s = MISSION_STATUSES.find((x) => x.v === status);
  return <span className={`status-pill ${s?.c || "status-brouillon"}`}>{s?.l || status}</span>;
}

function EqStatusBadge({ status }) {
  const s = EQ_STATUSES.find((x) => x.v === status);
  return <span className={`status-pill ${s?.c || "status-brouillon"}`}>{s?.l || status}</span>;
}

/* ============ MISSIONS ============ */
function MissionsTab({ projects }) {
  const [missions, setMissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [travels, setTravels] = useState([]);
  const [accs, setAccs] = useState([]);

  const refresh = async () => {
    const [m, u] = await Promise.all([api.get("/missions"), api.get("/users")]);
    setMissions(m.data); setUsers(u.data);
  };
  useEffect(() => { refresh(); }, []);

  const openMission = async (m) => {
    setSelected(m);
    const [t, a] = await Promise.all([api.get(`/travels?mission_id=${m.id}`), api.get(`/accommodations?mission_id=${m.id}`)]);
    setTravels(t.data); setAccs(a.data);
  };

  const toggleCheck = async (idx) => {
    const r = await api.patch(`/missions/${selected.id}/checklist?index=${idx}`);
    setSelected({ ...selected, checklist: r.data.checklist });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">{missions.length} mission(s)</div>
          <button onClick={() => setShowCreate(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-9 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="create-mission-btn">
            <Plus size={14} /> Nouvelle
          </button>
        </div>
        {missions.map((m) => {
          const project = projects.find((p) => p.id === m.project_id);
          return (
            <button key={m.id} onClick={() => openMission(m)}
              className={`w-full text-left bg-white border rounded-md p-4 transition-all ${selected?.id === m.id ? "border-btp-navy shadow-md" : "border-slate-200 hover:border-slate-300"}`}
              data-testid={`mission-card-${m.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="font-mono text-[10px] text-slate-500">{project?.code}</div>
                <MissionStatusBadge status={m.status} />
              </div>
              <h3 className="font-heading font-bold text-sm leading-snug mb-1">{m.name}</h3>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <MapPin size={11} /> {m.location}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 font-mono">{fmtDate(m.start_date)} → {fmtDate(m.end_date)}</div>
            </button>
          );
        })}
        {missions.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Aucune mission planifiée</div>}
      </div>

      <div className="lg:col-span-2">
        {selected ? <MissionDetail mission={selected} users={users} travels={travels} accs={accs} onToggle={toggleCheck} onRefresh={async () => { await refresh(); openMission(selected); }} /> :
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
            <Briefcase className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Sélectionnez une mission pour voir les détails.</p>
          </div>}
      </div>

      {showCreate && <MissionModal projects={projects} users={users} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
    </div>
  );
}

function MissionDetail({ mission, users, travels, accs, onToggle, onRefresh }) {
  const team = users.filter((u) => (mission.team_ids || []).includes(u.id));
  const totalTravel = travels.reduce((s, t) => s + (t.cost || 0), 0);
  const totalAcc = accs.reduce((s, a) => s + (a.cost || 0), 0);
  const doneCount = (mission.checklist || []).filter((c) => c.done).length;
  const [innerTab, setInnerTab] = useState("apercu");
  const [showTravel, setShowTravel] = useState(false);
  const [showAcc, setShowAcc] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [showAssignEq, setShowAssignEq] = useState(false);

  const refreshEquipment = async () => {
    const r = await api.get("/equipment");
    setAllEquipment(r.data);
    setEquipment(r.data.filter((e) => e.current_mission_id === mission.id));
  };
  useEffect(() => { refreshEquipment(); /* eslint-disable-next-line */ }, [mission.id]);

  const assignEq = async (eqId) => {
    await api.patch(`/equipment/${eqId}/assign?mission_id=${mission.id}`);
    toast.success("Matériel assigné");
    refreshEquipment();
  };
  const unassignEq = async (eqId) => {
    await api.patch(`/equipment/${eqId}/assign?mission_id=`);
    toast.success("Matériel libéré");
    refreshEquipment();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <MissionStatusBadge status={mission.status} />
            <h2 className="font-heading text-xl font-bold tracking-tight mt-2">{mission.name}</h2>
            <div className="flex items-center gap-1 text-sm text-slate-600 mt-1"><MapPin size={13} /> {mission.location} · {mission.country}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{fmtDate(mission.start_date)} → {fmtDate(mission.end_date)}</div>
          </div>
          <button onClick={async () => {
            if (window.confirm("Supprimer cette mission ?")) {
              await api.delete(`/missions/${mission.id}`); toast.success("Supprimé"); onRefresh();
            }
          }} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <button onClick={async () => {
            try {
              const res = await api.get(`/missions/${mission.id}/calendar.ics`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a"); a.href = url; a.download = `mission-${mission.name.replace(/\s+/g, '_')}.ics`; a.click();
              toast.success("Fichier .ics téléchargé");
            } catch { toast.error("Erreur"); }
          }} className="text-xs text-btp-navy font-semibold flex items-center gap-1 hover:underline" data-testid={`mission-ics-${mission.id}`}>
            <CalendarPlus size={12} /> Ajouter au calendrier (.ics)
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
        <Stat label="Déplacements" value={`${travels.length} · ${fmtMoney(totalTravel)}`} mono />
        <Stat label="Hébergements" value={`${accs.length} · ${fmtMoney(totalAcc)}`} mono />
        <Stat label="Matériel" value={`${equipment.length} pièce${equipment.length > 1 ? "s" : ""}`} mono />
        <Stat label="Checklist" value={`${doneCount}/${(mission.checklist || []).length}`} mono accent={doneCount < (mission.checklist || []).length} />
      </div>

      {/* Inner tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto bg-white rounded-t-md">
        <InnerTabBtn active={innerTab === "apercu"} onClick={() => setInnerTab("apercu")} icon={ListChecks} testid="mtab-apercu">Aperçu</InnerTabBtn>
        <InnerTabBtn active={innerTab === "travels"} onClick={() => setInnerTab("travels")} icon={Plane} testid="mtab-travels">Déplacements <span className="ml-1 text-[10px] text-slate-500">({travels.length})</span></InnerTabBtn>
        <InnerTabBtn active={innerTab === "accs"} onClick={() => setInnerTab("accs")} icon={Hotel} testid="mtab-accs">Hébergements <span className="ml-1 text-[10px] text-slate-500">({accs.length})</span></InnerTabBtn>
        <InnerTabBtn active={innerTab === "eq"} onClick={() => setInnerTab("eq")} icon={Wrench} testid="mtab-eq">Matériel <span className="ml-1 text-[10px] text-slate-500">({equipment.length})</span></InnerTabBtn>
      </div>

      {innerTab === "apercu" && (
        <>
          {mission.objectives && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Objectifs</div>
              <p className="text-sm text-slate-700 leading-relaxed">{mission.objectives}</p>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3 flex items-center gap-2"><Users2 size={12} /> Équipe terrain ({team.length})</div>
            <div className="flex flex-wrap gap-2">
              {team.map((u) => (
                <div key={u.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pr-3 py-0.5">
                  <div className="w-7 h-7 rounded-full bg-btp-navy text-white flex items-center justify-center text-[10px] font-semibold">{initials(u.full_name)}</div>
                  <div className="text-sm">{u.full_name}<span className="text-xs text-slate-500 ml-1">· {u.expertise || u.role}</span></div>
                </div>
              ))}
              {team.length === 0 && <span className="text-sm text-slate-400">Aucun membre</span>}
            </div>
          </div>
          {(mission.checklist || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3 flex items-center gap-2"><ListChecks size={12} /> Checklist préparation</div>
              <div className="space-y-1.5">
                {(mission.checklist || []).map((c, i) => (
                  <button key={i} onClick={() => onToggle(i)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded text-left"
                    data-testid={`check-${i}`}>
                    {c.done ? <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" /> : <Circle size={16} className="text-slate-300 flex-shrink-0" />}
                    <span className={`text-sm ${c.done ? "line-through text-slate-400" : "text-slate-800"}`}>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {innerTab === "travels" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h4 className="font-heading text-sm font-bold flex items-center gap-2"><Plane size={14} /> Déplacements de la mission</h4>
            <button onClick={() => setShowTravel(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-8 rounded text-xs font-semibold flex items-center gap-1" data-testid="add-travel-mission-btn">
              <Plus size={13} /> Ajouter
            </button>
          </div>
          <table className="btp-table">
            <thead><tr><th>Personne</th><th>Mode</th><th>Trajet</th><th>Départ</th><th>Statut</th><th>Coût</th><th></th></tr></thead>
            <tbody>
              {travels.map((t) => {
                const u = users.find((x) => x.id === t.user_id);
                return (
                  <tr key={t.id} data-testid={`mission-travel-${t.id}`}>
                    <td className="text-xs">{u?.full_name || "—"}</td>
                    <td className="text-xs uppercase">{t.mode}</td>
                    <td className="text-sm">{t.from_location} → {t.to_location}</td>
                    <td className="text-xs font-mono">{fmtDate(t.departure_at)}</td>
                    <td className="text-xs uppercase">{t.status}</td>
                    <td className="text-xs font-mono">{fmtMoney(t.cost)}</td>
                    <td>
                      <button onClick={async () => { await api.delete(`/travels/${t.id}`); toast.success("Supprimé"); onRefresh(); }} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
              {travels.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8 text-sm">Aucun déplacement</td></tr>}
            </tbody>
          </table>
          {showTravel && <TravelModal missions={[mission]} users={users} forcedMissionId={mission.id} onClose={() => setShowTravel(false)} onCreated={() => { setShowTravel(false); onRefresh(); }} />}
        </div>
      )}

      {innerTab === "accs" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h4 className="font-heading text-sm font-bold flex items-center gap-2"><Hotel size={14} /> Hébergements de la mission</h4>
            <button onClick={() => setShowAcc(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-8 rounded text-xs font-semibold flex items-center gap-1" data-testid="add-acc-mission-btn">
              <Plus size={13} /> Ajouter
            </button>
          </div>
          <table className="btp-table">
            <thead><tr><th>Personne</th><th>Hôtel</th><th>Ville</th><th>Du / Au</th><th>Nuits</th><th>Coût</th><th></th></tr></thead>
            <tbody>
              {accs.map((a) => {
                const u = users.find((x) => x.id === a.user_id);
                return (
                  <tr key={a.id} data-testid={`mission-acc-${a.id}`}>
                    <td className="text-xs">{u?.full_name || "—"}</td>
                    <td className="text-sm font-medium">{a.hotel_name}</td>
                    <td className="text-xs">{a.city}</td>
                    <td className="text-xs font-mono">{fmtDate(a.check_in)} → {fmtDate(a.check_out)}</td>
                    <td className="text-xs font-mono text-center">{a.nights}</td>
                    <td className="text-xs font-mono">{fmtMoney(a.cost)}</td>
                    <td>
                      <button onClick={async () => { await api.delete(`/accommodations/${a.id}`); toast.success("Supprimé"); onRefresh(); }} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
              {accs.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8 text-sm">Aucun hébergement</td></tr>}
            </tbody>
          </table>
          {showAcc && <AccommodationModal missions={[mission]} users={users} forcedMissionId={mission.id} onClose={() => setShowAcc(false)} onCreated={() => { setShowAcc(false); onRefresh(); }} />}
        </div>
      )}

      {innerTab === "eq" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h4 className="font-heading text-sm font-bold flex items-center gap-2"><Wrench size={14} /> Matériel affecté</h4>
            <button onClick={() => setShowAssignEq(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-8 rounded text-xs font-semibold flex items-center gap-1" data-testid="assign-eq-btn">
              <Plus size={13} /> Assigner
            </button>
          </div>
          <table className="btp-table">
            <thead><tr><th>Matériel</th><th>Catégorie</th><th>N° série</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {equipment.map((e) => (
                <tr key={e.id} data-testid={`mission-eq-${e.id}`}>
                  <td className="text-sm font-medium">{e.name}</td>
                  <td><span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 rounded">{e.category}</span></td>
                  <td className="text-xs font-mono text-slate-500">{e.serial || "—"}</td>
                  <td><EqStatusBadge status={e.status} /></td>
                  <td>
                    <button onClick={() => unassignEq(e.id)} className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid={`unassign-eq-${e.id}`}>
                      <X size={12} /> Libérer
                    </button>
                  </td>
                </tr>
              ))}
              {equipment.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-8 text-sm">Aucun matériel affecté à cette mission</td></tr>}
            </tbody>
          </table>
          {showAssignEq && (
            <AssignEquipmentModal
              available={allEquipment.filter((e) => !e.current_mission_id || e.current_mission_id === "")}
              onAssign={async (id) => { await assignEq(id); setShowAssignEq(false); }}
              onClose={() => setShowAssignEq(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function InnerTabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap ${active ? "text-btp-navy border-btp-navy bg-slate-50" : "text-slate-500 border-transparent hover:text-slate-700"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

function AssignEquipmentModal({ available, onAssign, onClose }) {
  const [q, setQ] = useState("");
  const filtered = available.filter((e) => !q || e.name.toLowerCase().includes(q.toLowerCase()) || (e.serial || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Assigner du matériel</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-4">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, n° série)…"
            className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="assign-eq-search" />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
          {filtered.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Aucun matériel disponible.</div>}
          {filtered.map((e) => (
            <button key={e.id} onClick={() => onAssign(e.id)}
              className="w-full text-left px-3 py-2.5 border border-slate-200 rounded hover:border-btp-navy hover:bg-slate-50 flex items-center justify-between gap-3"
              data-testid={`assign-eq-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{e.name}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">{e.category} · {e.serial || "sans n° série"}</div>
              </div>
              <Plus size={14} className="text-btp-terra flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MissionModal({ projects, users, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: projects[0]?.id || "", name: "", location: "", region: "", country: "Sénégal",
    start_date: "", end_date: "", objectives: "", team_ids: [], status: "planifiee",
    checklist: [{ label: "Réserver véhicules", done: false }, { label: "Confirmer hébergements", done: false }, { label: "Vérifier matériel", done: false }],
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleTeam = (id) => setForm({ ...form, team_ids: form.team_ids.includes(id) ? form.team_ids.filter((x) => x !== id) : [...form.team_ids, id] });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/missions", form); toast.success("Mission créée"); onCreated(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mission-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <h2 className="font-heading text-xl font-bold">Nouvelle mission terrain</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <select required value={form.project_id} onChange={update("project_id")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="mission-project">
            <option value="">— Projet —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
          <input required placeholder="Nom de la mission" value={form.name} onChange={update("name")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="mission-name" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Lieu" required value={form.location} onChange={update("location")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
            <input placeholder="Région" value={form.region} onChange={update("region")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
            <input type="date" required value={form.start_date} onChange={update("start_date")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
            <input type="date" required value={form.end_date} onChange={update("end_date")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          </div>
          <textarea rows={3} placeholder="Objectifs" value={form.objectives} onChange={update("objectives")} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Équipe terrain</div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded">
              {users.filter((u) => u.role !== "client").map((u) => (
                <button key={u.id} type="button" onClick={() => toggleTeam(u.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${form.team_ids.includes(u.id) ? "bg-btp-navy text-white border-btp-navy" : "bg-white text-slate-700 border-slate-300"}`}>
                  {u.full_name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold flex items-center justify-center gap-2" data-testid="submit-mission">
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Créer"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}


function TravelModal({ missions, users, forcedMissionId, onClose, onCreated }) {
  const [form, setForm] = useState({ mission_id: forcedMissionId || "", user_id: "", mode: "voiture", from_location: "", to_location: "", departure_at: "", arrival_at: "", cost: 0, reference: "", status: "prevu", notes: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/travels", { ...form, cost: Number(form.cost) }); toast.success("Ajouté"); onCreated(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-lg p-6 space-y-3">
        <h2 className="font-heading text-xl font-bold">Nouveau déplacement</h2>
        {!forcedMissionId && (
          <select required value={form.mission_id} onChange={update("mission_id")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
            <option value="">— Mission —</option>{missions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <select required value={form.user_id} onChange={update("user_id")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          <option value="">— Personne —</option>{users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <select value={form.mode} onChange={update("mode")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          {["avion","voiture","bus","train","bateau","autre"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Départ" value={form.from_location} onChange={update("from_location")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input required placeholder="Arrivée" value={form.to_location} onChange={update("to_location")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input required type="date" value={form.departure_at} onChange={update("departure_at")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input type="date" value={form.arrival_at} onChange={update("arrival_at")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input type="number" min="0" placeholder="Coût ($)" value={form.cost} onChange={update("cost")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input placeholder="Référence" value={form.reference} onChange={update("reference")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
          <button type="submit" className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold" data-testid="submit-travel">Ajouter</button>
        </div>
      </form>
    </div>
  );
}


function AccommodationModal({ missions, users, forcedMissionId, onClose, onCreated }) {
  const [form, setForm] = useState({ mission_id: forcedMissionId || "", user_id: "", hotel_name: "", address: "", city: "", check_in: "", check_out: "", nights: 1, cost: 0, booking_ref: "", status: "prevu", notes: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const nights = form.check_in && form.check_out ? Math.max(1, Math.ceil((new Date(form.check_out) - new Date(form.check_in)) / 86400000)) : Number(form.nights || 1);
    try { await api.post("/accommodations", { ...form, nights, cost: Number(form.cost) }); toast.success("Ajouté"); onCreated(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-lg p-6 space-y-3">
        <h2 className="font-heading text-xl font-bold">Nouvel hébergement</h2>
        <select required value={form.mission_id} onChange={update("mission_id")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          <option value="">— Mission —</option>{missions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select required value={form.user_id} onChange={update("user_id")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          <option value="">— Personne —</option>{users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <input required placeholder="Nom de l'hôtel" value={form.hotel_name} onChange={update("hotel_name")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Adresse" value={form.address} onChange={update("address")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input placeholder="Ville" value={form.city} onChange={update("city")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input type="date" required value={form.check_in} onChange={update("check_in")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input type="date" required value={form.check_out} onChange={update("check_out")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input type="number" min="0" placeholder="Coût ($)" value={form.cost} onChange={update("cost")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
          <input placeholder="Réf. réservation" value={form.booking_ref} onChange={update("booking_ref")} className="h-10 px-3 border border-slate-300 rounded text-sm" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
          <button type="submit" className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold" data-testid="submit-accommodation">Ajouter</button>
        </div>
      </form>
    </div>
  );
}

/* ============ EQUIPMENT ============ */
function EquipmentTab() {
  const [items, setItems] = useState([]);
  const [missions, setMissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const refresh = async () => {
    const params = new URLSearchParams();
    if (catFilter !== "all") params.set("category", catFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (q) params.set("q", q);
    const [e, m, u] = await Promise.all([api.get(`/equipment?${params.toString()}`), api.get("/missions"), api.get("/users")]);
    setItems(e.data); setMissions(m.data); setUsers(u.data);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [catFilter, statusFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()}
          placeholder="Rechercher (nom, n° série…)" className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white flex-1 min-w-[220px]" data-testid="eq-search" />
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white">
          <option value="all">Toutes catégories</option>
          {EQ_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white">
          <option value="all">Tous statuts</option>
          {EQ_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <button onClick={() => setShowModal(true)} className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-10 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="add-eq-btn">
          <Plus size={14} /> Ajouter matériel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it) => {
          const m = missions.find((x) => x.id === it.current_mission_id);
          const u = users.find((x) => x.id === it.current_user_id);
          return (
            <div key={it.id} className="bg-white border border-slate-200 rounded-md p-4 hover:border-btp-navy transition-colors" data-testid={`eq-card-${it.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-700 rounded">{it.category}</div>
                <EqStatusBadge status={it.status} />
              </div>
              <h4 className="font-heading font-bold text-sm leading-snug">{it.name}</h4>
              {it.serial && <div className="text-[11px] text-slate-500 font-mono mt-0.5">N° série: {it.serial}</div>}
              {it.description && <div className="text-xs text-slate-600 mt-2 line-clamp-2">{it.description}</div>}
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Localisation</div>
                  <div className="truncate">{it.location || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Affecté à</div>
                  <div className="truncate">{u?.full_name || m?.name || "—"}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/equipment/${it.id}`); refresh(); } }} className="text-slate-400 hover:text-red-600 text-xs flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="md:col-span-2 xl:col-span-3 bg-white border border-dashed border-slate-300 rounded-md p-12 text-center"><Wrench className="w-10 h-10 mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">Aucun matériel</p></div>}
      </div>
      {showModal && <EquipmentModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); refresh(); }} />}
    </div>
  );
}

function EquipmentModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", category: "topographie", serial: "", description: "", status: "disponible", location: "Dépôt" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/equipment", form); toast.success("Ajouté"); onCreated(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-md p-6 space-y-3">
        <h2 className="font-heading text-xl font-bold">Nouveau matériel</h2>
        <input required placeholder="Nom" value={form.name} onChange={update("name")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" data-testid="eq-name" />
        <select value={form.category} onChange={update("category")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          {EQ_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input placeholder="Numéro de série" value={form.serial} onChange={update("serial")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" />
        <textarea rows={2} placeholder="Description" value={form.description} onChange={update("description")} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
        <input placeholder="Localisation" value={form.location} onChange={update("location")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm" />
        <select value={form.status} onChange={update("status")} className="w-full h-10 px-3 border border-slate-300 rounded text-sm">
          {EQ_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 h-10 border border-slate-300 rounded text-sm">Annuler</button>
          <button type="submit" className="flex-1 h-10 bg-btp-terra text-white rounded text-sm font-semibold" data-testid="submit-eq">Ajouter</button>
        </div>
      </form>
    </div>
  );
}
